
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
    ChevronLeft,
    ChevronRight,
    Calendar,
    AlertTriangle,
    Ban,
    Clock,
    User,
    Car,
    DollarSign,
    MapPin, Gauge, Tag, CreditCard, Shield, FileText, Camera,
    Search
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './reservationform.css';

const initialForm = {
    client_name: '',
    client_id: null,
    voiture_id: null,
    lieu_depart: 'local',
    lieu_retour: 'local',
    km_depart: '',
    km_retour: '',
    date_depart: null,
    date_retour_prevue: null,
    date_retour_effective: '',
    prix_base: 0,
    remise: 0,
    remise_description: '',
    total: 0,
    total_paye: 0,
    mode_paiement: 'versement',
    reste_a_payer: 0,
    versement: 0,
    montant_daman: 0,
    daman_rendu: true,
    status: 'en_attente',
    agent_reservation: null,
    heure_depart: '08:00',
    heure_retour_prevue: '18:00'
};

const ReservationForm = ({ onSuccess, onError, onCancel }) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [form, setForm] = useState(initialForm);
    const [clients, setClients] = useState([]);
    const [voitures, setVoitures] = useState([]);
    const [clientStats, setClientStats] = useState(null);
    const [reservedPeriods, setReservedPeriods] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [success, setSuccess] = useState('');
    const [selectingStart, setSelectingStart] = useState(true);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showTimePicker, setShowTimePicker] = useState(false);
    const [calendarType, setCalendarType] = useState('depart'); // 'depart' or 'retour'
    const [clientSearch, setClientSearch] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);

    // Calendar state
    const [currentDate, setCurrentDate] = useState(new Date());
    const [hoveredDate, setHoveredDate] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [clientsRes, voituresRes] = await Promise.all([
                    axios.get('https://krili-loto.com/api/clients'),
                    axios.get('https://krili-loto.com/api/voitures')
                ]);
                setClients(clientsRes.data);
                setVoitures(voituresRes.data);
            } catch (err) {
                console.error("Error fetching data:", err);
            }
        };
        fetchData();
    }, []);

    // Filter clients based on search term
    const filteredClients = useMemo(() => {
        if (!clientSearch) return clients;
        return clients.filter(client =>
            `${client.nom} ${client.prenom}`.toLowerCase().includes(clientSearch.toLowerCase()) ||
            client.telephone?.includes(clientSearch)
        );
    }, [clients, clientSearch]);

    useEffect(() => {
        if (form.client_id) {
            axios.get(`https://krili-loto.com/api/reservations/client/${form.client_id}/stats`)
                .then(res => setClientStats(res.data))
                .catch(() => setClientStats(null));
        }
    }, [form.client_id]);

    // Rest of your existing code remains the same...

    const handleClientSelect = (clientId, clientName) => {
        setForm(prev => ({
            ...prev,
            client_id: clientId,
            client_name: clientName
        }));
        setClientSearch(clientName);
        setShowClientDropdown(false);
    };

    const handleClientSearchChange = (e) => {
        const value = e.target.value;
        setClientSearch(value);
        setShowClientDropdown(true);

        // If clearing the search, also clear the selected client
        if (!value) {
            setForm(prev => ({
                ...prev,
                client_id: null,
                client_name: ''
            }));
        }
    };

    useEffect(() => {
        if (!form.voiture_id) return;

        // Fetch reserved periods for the selected car using the new API endpoint
        axios.get(`https://krili-loto.com/api/reservations/periods/${form.voiture_id}`)
            .then(res => {
                console.log('Reserved periods:', res.data);
                setReservedPeriods(res.data);
            })
            .catch(err => {
                console.error('Error fetching reserved periods:', err);
                setReservedPeriods([]);
            });

        const selectedCar = voitures.find(v => v.id === form.voiture_id);
        if (!selectedCar) return;

        if (form.date_depart && form.date_retour_prevue) {
            const startDateTime = moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`);
            const endDateTime = moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`);

            const hours = Math.ceil(endDateTime.diff(startDateTime, 'hours', true));
            const days = Math.max(1, Math.ceil(hours / 24));

            const dailyRate = selectedCar.prix_location_jour;
            const totalBeforeDiscount = dailyRate * days;

            const safeRemise = Math.min(form.remise, totalBeforeDiscount);
            const newTotal = totalBeforeDiscount - safeRemise;

            // Calculate payment values based on current payment mode
            let totalPaye = form.total_paye;
            let resteAPayer = form.reste_a_payer;
            let versement = form.versement;

            if (form.mode_paiement === 'paye') {
                totalPaye = newTotal;
                versement = 0;
                resteAPayer = 0;
            } else if (form.mode_paiement === 'non_paye') {
                totalPaye = 0;
                versement = 0;
                resteAPayer = newTotal;
            } else if (form.mode_paiement === 'versement') {
                // For 'versement', adjust the remaining amount
                versement = Math.min(form.versement, newTotal);
                totalPaye = versement;
                resteAPayer = newTotal - versement;
            }

            setForm(prev => ({
                ...prev,
                prix_base: totalBeforeDiscount,
                remise: safeRemise,
                total: newTotal,
                total_paye: totalPaye,
                versement: versement,
                reste_a_payer: resteAPayer
            }));
        }
    }, [
        form.voiture_id,
        form.date_depart,
        form.date_retour_prevue,
        form.heure_depart,
        form.heure_retour_prevue,
        form.remise,
        form.mode_paiement,
        form.versement
    ]);
    useEffect(() => {
        const safeRemise = Math.min(form.remise, form.total);
        const newTotal = form.prix_base - safeRemise;
        const remaining = newTotal - (form.mode_paiement === 'versement' ? form.versement : 0);

        setForm(prev => ({
            ...prev,
            remise: safeRemise,
            total: newTotal,
            total_paye: prev.mode_paiement === 'paye' ? newTotal : prev.total_paye,
            reste_a_payer: remaining
        }));
    }, [form.remise, form.prix_base, form.mode_paiement, form.versement]);

    const isDateInPeriod = (date, period) => {
        const checkDate = new Date(date);
        const startDate = new Date(period.start);
        const endDate = new Date(period.end);

        return checkDate >= startDate && checkDate <= endDate;
    };

    const getDateStatus = (date) => {
        for (const period of reservedPeriods) {
            if (isDateInPeriod(date, period)) {
                return period.status;
            }
        }
        return null;
    };

    const isDateSelectable = (date) => {
        const status = getDateStatus(date);
        return status !== 'en_cours';
    };

    const handleDateClick = (date) => {
        const status = getDateStatus(date);

        if (status === 'en_cours') {
            return; // Toujours bloquer les dates avec réservation en cours
        }

        if (status === 'en_attente') {
            const confirmed = window.confirm(t('pending_date_warning'));
            if (!confirmed) return;
        }

        // ✅ Plus de vérification des dates passées
        if (calendarType === 'depart') {
            setForm(prev => ({
                ...prev,
                date_depart: date,
                date_retour_prevue: null
            }));
            setSelectingStart(false);
        } else {
            setForm(prev => ({
                ...prev,
                date_retour_prevue: date
            }));
            setSelectingStart(true);
        }
        setShowCalendar(false);
        setShowTimePicker(true);
    };
    const handleTimeChange = (e, type) => {
        const { value } = e.target;
        if (type === 'depart') {
            setForm(prev => ({ ...prev, heure_depart: value }));
        } else {
            setForm(prev => ({ ...prev, heure_retour_prevue: value }));
        }
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    const renderCalendar = () => {
        const days = getDaysInMonth(currentDate);
        const monthNames = [
            t('january'), t('february'), t('march'), t('april'), t('may'), t('june'),
            t('july'), t('august'), t('september'), t('october'), t('november'), t('december')
        ];
        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];

        return (
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 absolute z-10 mt-1 w-full max-w-md">
                <div className="flex items-center justify-between mb-4">
                    <button
                        type="button"
                        onClick={() => navigateMonth(-1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>

                    <h2 className="text-lg font-semibold text-gray-800">
                        {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h2>

                    <button
                        type="button"
                        onClick={() => navigateMonth(1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className={`flex flex-wrap gap-3 mb-4 text-xs ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <div className="w-3 h-3 bg-red-500 rounded"></div>
                        <span className="text-gray-600">{t('in_progress_no_select')}</span>
                    </div>
                    <div className={`flex items-center gap-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <div className="w-3 h-3 bg-yellow-400 rounded"></div>
                        <span className="text-gray-600">{t('pending_with_alert')}</span>
                    </div>
                    <div className={`flex items-center gap-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                        <span className="text-gray-600">{t('available')}</span>
                    </div>
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {dayNames.map((day) => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                            {day}
                        </div>
                    ))}

                    {days.map((date, index) => {
                        if (!date) {
                            return <div key={index} className="aspect-square"></div>;
                        }

                        const status = getDateStatus(date);
                        const isSelectable = isDateSelectable(date);
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isPast = date < new Date().setHours(0, 0, 0, 0);
                        const isSelectedStart = form.date_depart && date.toDateString() === form.date_depart.toDateString();
                        const isSelectedEnd = form.date_retour_prevue && date.toDateString() === form.date_retour_prevue.toDateString();

                        let cellClasses = 'aspect-square flex items-center justify-center text-sm rounded-lg transition-all duration-200 relative ';

                        if (isPast && !isSelectable) {
                            cellClasses += 'text-gray-400 cursor-not-allowed bg-gray-50 ';
                        } else if (status === 'en_cours') {
                            cellClasses += 'bg-red-500 text-white cursor-not-allowed ';
                        } else if (status === 'en_attente') {
                            cellClasses += 'bg-yellow-400 text-black cursor-pointer hover:bg-yellow-500 ';
                        } else if (isSelectable) {
                            cellClasses += 'hover:bg-blue-50 cursor-pointer text-gray-700 ';
                        } else {
                            cellClasses += 'cursor-not-allowed text-gray-400 ';
                        }

                        if (isToday) {
                            cellClasses += 'ring-2 ring-blue-400 ';
                        }

                        if (isSelectedStart || isSelectedEnd) {
                            cellClasses += 'bg-blue-500 text-white ring-2 ring-blue-600 ';
                        }

                        return (
                            <div
                                key={index}
                                className={cellClasses}
                                onClick={() => isSelectable && handleDateClick(date)}
                                onMouseEnter={() => setHoveredDate(date)}
                                onMouseLeave={() => setHoveredDate(null)}
                            >
                                <span className="relative z-10">{date.getDate()}</span>

                                {status === 'en_cours' && (
                                    <Ban className="absolute top-0 right-0 w-3 h-3 text-white opacity-75" />
                                )}
                                {status === 'en_attente' && (
                                    <AlertTriangle className="absolute top-0 right-0 w-3 h-3 text-orange-600" />
                                )}
                            </div>
                        );
                    })}
                </div>

                {hoveredDate && (
                    <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                        {(() => {
                            const status = getDateStatus(hoveredDate);
                            if (status === 'en_cours') {
                                return (
                                    <div className={`flex items-center gap-2 text-red-600 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <Ban className="w-4 h-4" />
                                        <span>{t('date_unavailable_in_progress')}</span>
                                    </div>
                                );
                            }
                            if (status === 'en_attente') {
                                return (
                                    <div className={`flex items-center gap-2 text-yellow-600 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <AlertTriangle className="w-4 h-4" />
                                        <span>{t('pending_click_continue')}</span>
                                    </div>
                                );
                            }
                            return (
                                <div className={`flex items-center gap-2 text-green-600 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <Calendar className="w-4 h-4" />
                                    <span>{hoveredDate.toLocaleDateString(i18n.language)} - {t('available')}</span>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>
        );
    };

    const renderTimePicker = () => {
        return (
            <div className={`bg-white border border-gray-200 rounded-lg shadow-lg p-4 absolute z-50 mt-1 w-full max-w-xs ${isRtl ? 'text-right' : 'text-left'}`}>
                <h3 className="text-md font-semibold mb-3">
                    {t('select_time')} {calendarType === 'depart' ? t('departure') : t('return')}
                </h3>
                <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <Clock className="text-gray-600" />
                    <input
                        type="time"
                        value={calendarType === 'depart' ? form.heure_depart : form.heure_retour_prevue}
                        onChange={(e) => handleTimeChange(e, calendarType)}
                        className="p-2 border rounded"
                    />
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={() => setShowTimePicker(false)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        {t('confirm')}
                    </button>
                </div>
            </div>
        );
    };

    const validateForm = () => {
        const newErrors = [];

        // Format dates with time
        const departDateTime = moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`);
        const retourPrevueDateTime = moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`);

        if (!form.client_id && !form.client_name?.trim()) {
            newErrors.push(t('client_required'));
        }

        if (!form.voiture_id) {
            newErrors.push(t('vehicle_required'));
        }

        if (!form.km_depart || form.km_depart < 0) {
            newErrors.push(t('departure_km_invalid'));
        }

        if (!form.date_depart) {
            newErrors.push(t('departure_date_required'));
        }

        if (!form.date_retour_prevue) {
            newErrors.push(t('return_date_required'));
        }

        if (form.date_depart && form.date_retour_prevue && departDateTime.isAfter(retourPrevueDateTime)) {
            newErrors.push(t('return_after_departure_error'));
        }

        if (form.remise < 0 || form.remise > form.prix_base) {
            newErrors.push(`${t('discount_range_error')} ${form.prix_base}`);
        }

        // Payment validation based on mode
        if (form.mode_paiement === 'versement') {
            if (form.versement <= 0) {
                newErrors.push(t('versement_min_error'));
            }
            if (form.versement > form.total) {
                newErrors.push(t('versement_max_error'));
            }
        } else if (form.mode_paiement === 'paye') {
            if (form.total_paye !== form.total) {
                newErrors.push(t('paid_mode_error'));
            }
        } else if (form.mode_paiement === 'non_paye') {
            if (form.total_paye !== 0) {
                newErrors.push(t('non_paid_mode_error'));
            }
        }

        setErrors(newErrors);
        return newErrors.length === 0;
    };

    const formatDateTimeDisplay = (date, time) => {
        if (!date) return t('not_defined');
        return `${moment(date).format('DD/MM/YYYY')} ${t('at')} ${time}`;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleVoitureSelect = async (e) => {
        const voitureId = parseInt(e.target.value);
        const selectedCar = voitures.find(v => v.id === voitureId);

        if (selectedCar) {
            setForm(prev => ({
                ...prev,
                voiture_id: voitureId,
                km_depart: selectedCar.kilometrage,
                prix_base: 0,
                total: 0,
                date_depart: null,
                date_retour_prevue: null
            }));
        }
    };

    const handlePaymentModeChange = (e) => {
        const mode = e.target.value;
        setForm(prev => {
            let totalPaye = prev.total_paye;
            let versement = prev.versement;
            let resteAPayer = prev.reste_a_payer;

            if (mode === 'paye') {
                totalPaye = prev.total;
                versement = 0;
                resteAPayer = 0;
            } else if (mode === 'non_paye') {
                totalPaye = 0;
                versement = 0;
                resteAPayer = prev.total;
            } else if (mode === 'versement') {
                // For 'versement', keep the current versement value
                totalPaye = Math.min(prev.versement, prev.total);
                resteAPayer = prev.total - prev.versement;
            }

            return {
                ...prev,
                mode_paiement: mode,
                total_paye: totalPaye,
                versement: versement,
                reste_a_payer: resteAPayer
            };
        });
    };

    const handleVersementChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setForm(prev => {
            const total = parseFloat(prev.total) || 0;
            const safeVersement = Math.min(value, total);
            const resteAPayer = total - safeVersement;

            return {
                ...prev,
                versement: safeVersement,
                total_paye: prev.mode_paiement === 'versement' ? safeVersement : prev.total_paye,
                reste_a_payer: resteAPayer
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors([]);
        setSuccess('');

        if (!validateForm()) {
            setSubmitting(false);
            return;
        }

        try {
            // Combine dates with times
            const date_depart_with_time = moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`).format('YYYY-MM-DD HH:mm:ss');
            const date_retour_prevue_with_time = moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`).format('YYYY-MM-DD HH:mm:ss');

            const formData = {
                ...form,
                agent_reservation: 1,
                client_id: form.client_id || null,
                client_name: form.client_id ? null : form.client_name?.trim(),
                voiture_id: parseInt(form.voiture_id),
                km_depart: parseInt(form.km_depart),
                km_retour: form.km_retour ? parseInt(form.km_retour) : null,
                prix_base: parseFloat(form.prix_base),
                remise: parseFloat(form.remise || 0),
                total: parseFloat(form.total),
                total_paye: parseFloat(form.total_paye || 0),
                versement: parseFloat(form.versement || 0),
                montant_daman: parseFloat(form.montant_daman || 0),
                daman_rendu: !!form.daman_rendu,
                status: 'en_attente',
                date_depart: date_depart_with_time,
                date_retour_prevue: date_retour_prevue_with_time,
                mode_paiement: form.mode_paiement // Ensure this is included
            };

            const response = await axios.post('https://krili-loto.com/api/reservations', formData);

            if (response.data.id && photos.length > 0) {
                const formDataPhotos = new FormData();
                photos.forEach(photo => {
                    formDataPhotos.append('photos', photo);
                });
                await axios.post(
                    `https://krili-loto.com/api/reservations/${response.data.id}/photos`,
                    formDataPhotos,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            }

            setSuccess('✅ ' + t('success_reservation_created'));
            setForm(initialForm);
            setPhotos([]);
            setReservedPeriods([]);

            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            const errorMsg = err.response?.data?.message ||
                err.response?.data?.error ||
                t('error_creating_reservation');
            setErrors([errorMsg]);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-6 bg-white rounded-xl shadow-lg" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="mb-8 text-center">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">{t('new_reservation')}</h1>
                <p className="text-gray-600">{t('fill_reservation_details')}</p>
            </div>

            {errors.length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <AlertTriangle className="h-5 w-5 text-red-500" />
                        </div>
                        <div className={`ml-3 ${isRtl ? 'text-right' : 'text-left'}`}>
                            <h3 className="text-sm font-medium text-red-800">{t('errors_to_fix')}</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <ul className="list-disc pl-5 space-y-1">
                                    {errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {success && (
                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">{success}</p>
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Client & Vehicle Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className={`text-lg font-semibold text-gray-800 mb-4 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <User className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('client_vehicle_info')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="relative">
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <User className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('client')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={clientSearch}
                                    onChange={handleClientSearchChange}
                                    onFocus={() => setShowClientDropdown(true)}
                                    placeholder={t('search_client_placeholder')}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                />
                            </div>

                            {showClientDropdown && filteredClients.length > 0 && (
                                <div
                                    className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {filteredClients.map(client => (
                                        <div
                                            key={client.id}
                                            className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                            onClick={() => handleClientSelect(client.id, `${client.nom} ${client.prenom}`)}
                                        >
                                            <div className="font-medium">{client.nom} {client.prenom}</div>
                                            {client.telephone && (
                                                <div className="text-sm text-gray-600">{t('phone_short')}: {client.telephone}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-2 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="client_name"
                                    value={form.client_name}
                                    onChange={handleChange}
                                    placeholder={t('new_client_name_placeholder')}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                />
                            </div>

                            {form.client_id && (
                                <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                                    {t('selected_client')}: <span className="font-medium">{form.client_name}</span>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Car className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('vehicle')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Car className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    name="voiture_id"
                                    value={form.voiture_id || ''}
                                    onChange={handleVoitureSelect}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                    required
                                >
                                    <option value="">-- {t('select_vehicle')} --</option>
                                    {voitures
                                        .filter(v => v.disponible === 1)
                                        .map(v => (
                                            <option key={v.id} value={v.id}>
                                                {v.marque} {v.modele} - {v.matricule} ({v.prix_location_jour}{t('currency')}/{t('day')})
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                        </div>
                    </div>

                    {clientStats && (
                        <div className="mt-4 bg-white p-3 rounded-lg border border-gray-200">
                            <h3 className={`text-sm font-medium text-gray-700 mb-2 ${isRtl ? 'text-right' : 'text-left'}`}>📊 {t('client_history')}</h3>
                            <div className={`grid grid-cols-3 gap-2 text-sm ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <div className="bg-yellow-50 p-2 rounded text-center">
                                    <p className="font-medium text-yellow-800">⏳ {t('pending')}</p>
                                    <p className="text-lg font-bold">{clientStats.en_attente || 0}</p>
                                </div>
                                <div className="bg-red-50 p-2 rounded text-center">
                                    <p className="font-medium text-red-800">🚗 {t('in_progress')}</p>
                                    <p className="text-lg font-bold">{clientStats.en_cours || 0}</p>
                                </div>
                                <div className="bg-green-50 p-2 rounded text-center">
                                    <p className="font-medium text-green-800">✅ {t('completed')}</p>
                                    <p className="text-lg font-bold">{clientStats.done || 0}</p>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Dates Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className={`text-lg font-semibold text-gray-800 mb-4 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <Calendar className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('rental_period')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="relative">
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Calendar className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('departure_date_time')}
                            </label>
                            <div
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer hover:border-blue-500"
                                onClick={() => {
                                    setCalendarType('depart');
                                    setShowCalendar(!showCalendar);
                                }}
                            >
                                <span className={form.date_depart ? "text-gray-800" : "text-gray-500"}>
                                    {formatDateTimeDisplay(form.date_depart, form.heure_depart)}
                                </span>
                                <div className="flex">
                                    <Clock className="w-5 h-5 text-gray-500" />
                                </div>
                            </div>
                            {showCalendar && calendarType === 'depart' && renderCalendar()}
                            {showTimePicker && calendarType === 'depart' && renderTimePicker()}
                        </div>

                        <div className="relative">
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Calendar className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('return_date_time')}
                            </label>
                            <div
                                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer ${!form.date_depart ? 'bg-gray-100' : 'hover:border-blue-500'}`}
                                onClick={() => {
                                    if (!form.date_depart) {
                                        alert(t('select_departure_first'));
                                        return;
                                    }
                                    setCalendarType('retour');
                                    setShowCalendar(!showCalendar);
                                }}
                            >
                                <span className={form.date_retour_prevue ? "text-gray-800" : "text-gray-500"}>
                                    {formatDateTimeDisplay(form.date_retour_prevue, form.heure_retour_prevue)}
                                </span>
                                <div className="flex">
                                    <Clock className="w-5 h-5 text-gray-500" />
                                </div>
                            </div>
                            {showCalendar && calendarType === 'retour' && renderCalendar()}
                            {showTimePicker && calendarType === 'retour' && renderTimePicker()}
                        </div>
                    </div>

                    {form.date_depart && form.date_retour_prevue && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className={`bg-white p-3 rounded-lg border border-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                                <label className="block text-xs font-medium text-gray-500 mb-1">{t('base_price_day')}</label>
                                <div className="flex items-center">
                                    <DollarSign className="h-4 w-4 text-gray-500 mr-1" />
                                    <span className="text-lg font-semibold">
                                        {form.voiture_id ? voitures.find(v => v.id === form.voiture_id)?.prix_location_jour || 0 : 0}{t('currency')}
                                    </span>
                                </div>
                            </div>
                            <div className={`bg-white p-3 rounded-lg border border-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                                <label className="block text-xs font-medium text-gray-500 mb-1">{t('number_of_days')}</label>
                                <span className="text-lg font-semibold">
                                    {Math.ceil(
                                        moment(`${moment(form.date_retour_prevue).format('YYYY-MM-DD')} ${form.heure_retour_prevue}`)
                                            .diff(
                                                moment(`${moment(form.date_depart).format('YYYY-MM-DD')} ${form.heure_depart}`),
                                                'hours',
                                                true
                                            ) / 24
                                    )}
                                </span>
                            </div>
                            <div className={`bg-white p-3 rounded-lg border border-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                                <label className="block text-xs font-medium text-gray-500 mb-1">{t('total_price_before_discount')}</label>
                                <div className="flex items-center">
                                    <DollarSign className="h-4 w-4 text-gray-500 mr-1" />
                                    <span className="text-lg font-semibold">{form.prix_base}{t('currency')}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Location Details Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className={`text-lg font-semibold text-gray-800 mb-4 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <MapPin className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('rental_details')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <MapPin className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('departure_location')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MapPin className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="lieu_depart"
                                    value={form.lieu_depart}
                                    onChange={handleChange}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                />
                            </div>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <MapPin className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('return_location')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MapPin className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="lieu_retour"
                                    value={form.lieu_retour}
                                    onChange={handleChange}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                />
                            </div>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Gauge className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('departure_mileage')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Gauge className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="km_depart"
                                    value={form.km_depart}
                                    onChange={handleChange}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                    min="0"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pricing Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className={`text-lg font-semibold text-gray-800 mb-4 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <DollarSign className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('pricing_payment')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Tag className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('discount')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <DollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="remise"
                                    value={form.remise}
                                    onChange={handleChange}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                    min="0"
                                    max={form.prix_base}
                                    step="0.01"
                                />
                            </div>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <DollarSign className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('total_after_discount')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <DollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="total"
                                    value={form.total}
                                    readOnly
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg bg-gray-100`}
                                />
                            </div>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <CreditCard className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('payment_mode')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <CreditCard className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    name="mode_paiement"
                                    value={form.mode_paiement}
                                    onChange={handlePaymentModeChange}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                >
                                    <option value="versement">{t('versement')}</option>
                                    <option value="paye">{t('paid')}</option>
                                    <option value="non_paye">{t('unpaid')}</option> {/* Add this option */}
                                </select>
                            </div>
                        </div>

                        {form.mode_paiement === 'versement' && (
                            <>
                                <div>
                                    <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <DollarSign className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                        {t('initial_versement')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="versement"
                                            value={form.versement}
                                            onChange={handleVersementChange}
                                            className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                            min="0"
                                            max={form.total}
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <DollarSign className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                        {t('remaining_amount')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            value={form.reste_a_payer}
                                            readOnly
                                            className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg bg-gray-100`}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {form.mode_paiement === 'paye' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <DollarSign className="mr-1 h-4 w-4 text-gray-500" />
                                    {t('paid_amount')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        name="total_paye"
                                        value={form.total_paye}
                                        readOnly
                                        className="pl-10 w-full py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        )}

                        {form.mode_paiement === 'non_paye' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <DollarSign className="mr-1 h-4 w-4 text-gray-500" />
                                    {t('paid_amount')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        value="0"
                                        readOnly
                                        className="pl-10 w-full py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Deposit & Notes Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h2 className={`text-lg font-semibold text-gray-800 mb-4 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <Shield className={`${isRtl ? 'ml-2' : 'mr-2'} h-5 w-5 text-blue-600`} />
                        {t('deposit_notes')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Shield className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                                {t('deposit')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <DollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    name="montant_daman"
                                    value={form.montant_daman}
                                    onChange={handleChange}
                                    className={`${isRtl ? 'pr-10 text-right' : 'pl-10 text-left'} w-full py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500`}
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-start pt-6">
                            <input
                                type="checkbox"
                                name="daman_rendu"
                                checked={form.daman_rendu}  // Controlled by state
                                onChange={handleChange}     // Updates state
                                id="daman_rendu"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="daman_rendu" className={`${isRtl ? 'mr-2' : 'ml-2'} block text-sm text-gray-700`}>
                                {t('deposit_returned')}
                            </label>
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <FileText className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                            {t('description')}
                        </label>
                        <textarea
                            name="remise_description"
                            value={form.remise_description || ''}
                            onChange={handleChange}
                            className={`w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${isRtl ? 'text-right' : 'text-left'}`}
                            placeholder={t('notes_placeholder')}
                            rows={3}
                        />
                    </div>

                    <div className="mt-6">
                        <label className={`block text-sm font-medium text-gray-700 mb-1 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <Camera className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 text-gray-500`} />
                            {t('photos_optional')}
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                            <div className="space-y-1 text-center">
                                <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    stroke="currentColor"
                                    fill="none"
                                    viewBox="0 0 48 48"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div className="flex text-sm text-gray-600">
                                    <label
                                        htmlFor="file-upload"
                                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                                    >
                                        <span>{t('upload_files')}</span>
                                        <input
                                            id="file-upload"
                                            name="file-upload"
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={(e) => setPhotos([...e.target.files])}
                                            className="sr-only"
                                        />
                                    </label>
                                    <p className="pl-1">{t('drag_drop')}</p>
                                </div>
                                <p className="text-xs text-gray-500">{t('allowed_formats')} up to 10MB</p>
                            </div>
                        </div>
                        {photos.length > 0 && (
                            <div className="mt-2 text-sm text-gray-600">
                                {photos.length} {photos.length === 1 ? t('file') : t('files')} {t('selected')}
                            </div>
                        )}
                    </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                        {submitting ? (
                            <>
                                <svg className={`animate-spin ${isRtl ? 'ml-3' : '-ml-1 mr-3'} h-5 w-5 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('saving')}
                            </>
                        ) : t('create_reservation')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReservationForm;